import { useState } from 'react';
import { formatMoney, type PaymentMethodTypeValue } from '@teentack/shared';
import { EmptyState, StatTile } from '@/components/ui/Feedback';
import { Skeleton } from '@/components/ui/Skeleton';
import {
  useAddPaymentMethod,
  useMoneySummary,
  usePaymentMethods,
  useRemovePaymentMethod,
  useSetDefaultPaymentMethod,
  useTransactions,
} from '@/features/payments/use-payments';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input, SelectInput } from '@/components/ui/Input';
import { ErrorNote, InfoNote } from '@/components/Form';
import { WalletIcon } from '@/components/ui/Icons';
import { relativeTime } from '@/lib/relative-time';
import { presentApiError } from '@/lib/form-errors';

/**
 * Money. [Parent steps 4 and 22, Job Poster step 28]
 *
 * Everything that has been paid or received, plus the payment methods behind
 * it. The page states plainly that settlement is not yet connected, because a
 * screen that shows a ledger without saying so implies money moved.
 */

const TYPE_LABELS: Record<PaymentMethodTypeValue, string> = {
  CARD: 'Card',
  BANK: 'Bank account',
  WALLET: 'Wallet',
};

function AddMethodForm({ onDone }: { onDone: () => void }) {
  const [type, setType] = useState<PaymentMethodTypeValue>('CARD');
  const [providerToken, setProviderToken] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);

  const add = useAddPaymentMethod();

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setError(null);
    try {
      await add.mutateAsync({
        type,
        providerToken: providerToken.trim(),
        ...(label.trim() ? { label: label.trim() } : {}),
      });
      setProviderToken('');
      setLabel('');
      onDone();
    } catch (caught) {
      setError(presentApiError(caught, 'Could not save that method').message);
    }
  }

  return (
    <form
      onSubmit={(event) => void submit(event)}
      className="rounded-2xl border border-line bg-white p-5 shadow-soft"
    >
      <h3 className="text-sm font-bold text-ink">Add a payment method</h3>
      <p className="mt-1 text-xs leading-relaxed text-slate-500">
        Card numbers never reach TeenTack. Once a provider is connected, their secure form issues
        the token below and we store only that.
      </p>

      <div className="mt-4 grid gap-3 sm:grid-cols-2">
        <SelectInput
          label="Type"
          value={type}
          onChange={(event) => setType(event.target.value as PaymentMethodTypeValue)}
        >
          <option value="CARD">Card</option>
          <option value="BANK">Bank account</option>
          <option value="WALLET">Wallet</option>
        </SelectInput>
        <Input
          label="Label"
          value={label}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setLabel(event.target.value)}
          placeholder="Visa 4242"
          optional
        />
      </div>

      <div className="mt-3">
        <Input
          label="Provider token"
          value={providerToken}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setProviderToken(event.target.value)}
          placeholder="tok_…"
        />
      </div>

      {error && (
        <div className="mt-3">
          <ErrorNote message={error} />
        </div>
      )}

      <div className="mt-4">
        <Button type="submit" disabled={add.isPending || providerToken.trim().length < 8}>
          {add.isPending ? 'Saving…' : 'Save method'}
        </Button>
      </div>
    </form>
  );
}

export function MoneyPage() {
  const [adding, setAdding] = useState(false);

  const summary = useMoneySummary();
  const methods = usePaymentMethods();
  const transactions = useTransactions({ pageSize: 50 });
  const setDefault = useSetDefaultPaymentMethod();
  const remove = useRemovePaymentMethod();

  const currency = summary.data?.currency ?? 'USD';
  const items = transactions.data?.items ?? [];

  return (
    <main className="container-page py-10">
      <div className="mx-auto max-w-4xl">
        <header className="mb-8">
          <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
            Your account
          </p>
          <h1 className="mt-1 text-2xl font-extrabold tracking-tight text-ink sm:text-3xl">
            Money
          </h1>
          <p className="mt-2 max-w-prose text-sm leading-relaxed text-slate-500">
            Everything paid and received, and the methods behind it.
          </p>
        </header>

        {/* Said plainly rather than implied by an empty ledger. */}
        {summary.data && !summary.data.settlesRealMoney && (
          <div className="mb-8">
            <InfoNote>
              No payment provider is connected, so these records track what was agreed rather than
              money that has settled. Payments happen off-platform for now.
            </InfoNote>
          </div>
        )}

        {summary.data && (
          <section className="mb-10">
            <div className="grid gap-4 sm:grid-cols-3">
              <StatTile
                label="Received"
                value={formatMoney(summary.data.receivedMinor, currency)}
                hint="Across all finished work"
                tone={summary.data.receivedMinor > 0 ? 'good' : 'neutral'}
                icon={<WalletIcon className="size-4 text-slate-400" />}
              />
              <StatTile
                label="Still owed"
                value={formatMoney(summary.data.pendingMinor, currency)}
                hint="Agreed but not released"
                tone={summary.data.pendingMinor > 0 ? 'attention' : 'neutral'}
              />
              <StatTile
                label="Paid out"
                value={formatMoney(summary.data.paidMinor, currency)}
                hint="What you have paid"
              />
            </div>
          </section>
        )}

        {/* Methods */}
        <section className="mb-10">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-base font-bold text-ink">Payment methods</h2>
            {!adding && (
              <Button variant="outline" onClick={() => setAdding(true)}>
                Add a method
              </Button>
            )}
          </div>

          {adding && (
            <div className="mb-4">
              <AddMethodForm onDone={() => setAdding(false)} />
            </div>
          )}

          {methods.data && methods.data.length === 0 && !adding && (
            <EmptyState icon={<WalletIcon className="size-6" />} title="No methods saved">
              Add one so payments can be taken automatically once a provider is connected.
            </EmptyState>
          )}

          {methods.data && methods.data.length > 0 && (
            <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              {methods.data.map((method) => (
                <li
                  key={method.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink">
                      {method.label ?? TYPE_LABELS[method.type]}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {TYPE_LABELS[method.type]} · added {relativeTime(method.createdAt)}
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {method.isDefault ? (
                      <Badge tone="safe">Default</Badge>
                    ) : (
                      <Button
                        variant="ghost"
                        disabled={setDefault.isPending}
                        onClick={() => setDefault.mutate(method.id)}
                      >
                        Make default
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(method.id)}
                    >
                      Remove
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Ledger */}
        <section>
          <h2 className="mb-4 text-base font-bold text-ink">History</h2>

          {transactions.isError && (
            <ErrorNote
              message={presentApiError(transactions.error, 'Could not load your history').message}
            />
          )}

          {transactions.isPending && (
            <div className="space-y-3" aria-busy="true">
              <Skeleton className="h-16" />
              <Skeleton className="h-16" />
            </div>
          )}

          {transactions.data && items.length === 0 && (
            <EmptyState icon={<WalletIcon className="size-6" />} title="Nothing yet">
              Payments appear here once work is finished and signed off.
            </EmptyState>
          )}

          {items.length > 0 && (
            <ul className="divide-y divide-line overflow-hidden rounded-2xl border border-line bg-white shadow-soft">
              {items.map((entry) => (
                <li
                  key={entry.id}
                  className="flex flex-wrap items-center justify-between gap-3 px-5 py-4"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-bold text-ink">
                      {entry.jobTitle ?? entry.type.toLowerCase()}
                    </p>
                    <p className="mt-0.5 text-xs text-slate-500">
                      {entry.outgoing ? 'To' : 'From'} {entry.counterpartyName ?? 'TeenTack'} ·{' '}
                      {relativeTime(entry.createdAt)}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {entry.status !== 'SUCCESS' && (
                      <Badge tone={entry.status === 'FAILED' ? 'sun' : 'neutral'}>
                        {entry.status.charAt(0) + entry.status.slice(1).toLowerCase()}
                      </Badge>
                    )}
                    <span
                      className={`text-sm font-extrabold ${
                        entry.outgoing ? 'text-ink' : 'text-safe-700'
                      }`}
                      style={{ fontVariantNumeric: 'tabular-nums' }}
                    >
                      {entry.outgoing ? '−' : '+'}
                      {formatMoney(entry.amountMinor, entry.currency)}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </main>
  );
}
